import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

import { ref, watch } from 'vue';
import {
  GoogleMap, CustomMarker, Polyline,
} from 'vue3-google-map';
import { getLatLngCenter } from '../../helpers/mapping';
import {
  Port, GoogleMapsRoute, LatLng, MapPort, MapLocation,
} from '../../types';
import { googleMapApiKey } from '../../envVariables';
import { SMALL_DESKTOP_VIEWPORT_WIDTH } from '../../config';


export default /*#__PURE__*/_defineComponent({
  __name: 'Map',
  props: {
    center: {},
    routes: {},
    routeHoverItem: {},
    routeClickItem: {},
    ports: {},
    portHoverItem: {},
    portClickItem: {},
    destinations: {},
    destinationHoverItem: {},
    destinationClickItem: {},
    isStatic: { type: Boolean },
    zoom: {}
  },
  emits: [
  'mapClicked',
  'updateMapRef',
  'routeClicked',
  'routeHovered',
  'routeLeave',
  'portClicked',
  'portHovered',
  'portLeave',
  'destinationClicked',
  'destinationHovered',
  'destinationLeave',
  'zoomChange',
],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const apiKey = googleMapApiKey;

const emit = __emit;

const mapRef = ref();

const zoom = ref(props.zoom ?? 8);

const isSmallScreenWidth = window.innerWidth <= SMALL_DESKTOP_VIEWPORT_WIDTH;

const customMapStyle = [
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#444444',
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        color: '#f2f2f2',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        saturation: -100,
      },
      {
        lightness: 45,
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'all',
    stylers: [
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#BDE4E4',
      },
      {
        visibility: 'on',
      },
    ],
  },
];

const getIsPinItem = (pinItemCode, itemCode) => pinItemCode === itemCode;

const isNoPortSelected = (itemCode) => (!props.portHoverItem
  || !getIsPinItem(props.portHoverItem.portCode, itemCode)) && (
  !props.portClickItem || !getIsPinItem(props.portClickItem.portCode, itemCode)
);

const isNoDestinationSelected = (itemCode) => (!props.destinationHoverItem
  || !getIsPinItem(props.destinationHoverItem.code, itemCode)) && (
  !props.destinationClickItem || !getIsPinItem(props.destinationClickItem.code, itemCode)
);

const handlePortMouseover = (port) => {
/*
  Emit click event instead on mobile devices as hover state does not exist and using normal click
  event on Google Map Custom Marker which seems to only work when tapping twice on mobile devices,
  whereas mouseover event works when tapping once.
*/

  if (isSmallScreenWidth) {
    emit('portClicked', port);
    return;
  }
  emit('portHovered', port);
};

const handleDestinationMouseover = (destination) => {
/*
  Emit click event instead on mobile devices as hover state does not exist and using normal click
  event on Google Map Custom Marker which seems to only work when tapping twice on mobile devices,
  whereas mouseover event works when tapping once.
*/

  if (isSmallScreenWidth) {
    emit('destinationClicked', destination);
    return;
  }
  emit('destinationHovered', destination);
};

function getCenter() {
  let maxLat;
  let minLat;
  let maxLng;
  let minLng;

  if (props.center) {
    maxLat = props.center.lat;
    minLat = props.center.lat;
    maxLng = props.center.lng;
    minLng = props.center.lng;
    if (props.ports !== undefined) {
      maxLat = Math.max(...props.ports.map((l) => l.options.position.lat));
      minLat = Math.min(...props.ports.map((l) => l.options.position.lat));
      maxLng = Math.max(...props.ports.map((l) => l.options.position.lng));
      minLng = Math.min(...props.ports.map((l) => l.options.position.lng));
    } else if (props.destinations !== undefined) {
      maxLat = Math.max(...props.destinations.map((l) => l.options.position.lat));
      minLat = Math.min(...props.destinations.map((l) => l.options.position.lat));
      maxLng = Math.max(...props.destinations.map((l) => l.options.position.lng));
      minLng = Math.min(...props.destinations.map((l) => l.options.position.lng));
    }
  } else if (props.routes) {
    const routePorts = props.routes.map((route: GoogleMapsRoute) => {
      const result = route.ports.map((port: Port) => [port.latitude, port.longitude]);
      return result;
    }).flat();

    const location = getLatLngCenter(routePorts) as [number, number];
    maxLat = Number(location[0]);
    minLat = 0;
    maxLng = Number(location[1]);
    minLng = 0;
  }

  const center = { lat: (maxLat + minLat) / 2, lng: (maxLng + minLng) / 2 };
  return center;
}

watch(
  () => mapRef.value?.ready,
  (ready) => {
    if (!ready) return;
    emit('updateMapRef', mapRef.value);
  },
);


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(GoogleMap), {
    ref_key: "mapRef",
    ref: mapRef,
    "api-key": _unref(apiKey),
    class: "mapContainer",
    style: {"width":"100%","height":"100vh"},
    styles: customMapStyle,
    center: getCenter(),
    zoom: zoom.value,
    "disable-default-ui": _ctx.isStatic,
    draggable: !_ctx.isStatic,
    "street-view-control": false,
    "map-type-control": false,
    "fullscreen-control": false,
    onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('mapClicked'))),
    onZoom_changed: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('zoomChange', mapRef.value.map.getZoom(), zoom.value)))
  }, {
    default: _withCtx(() => [
      (_ctx.routes)
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(_ctx.routes, (route, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              _createVNode(_unref(Polyline), {
                options: (_ctx.routeHoverItem && _ctx.routeHoverItem.code === route.code) ||
            (_ctx.routeClickItem && _ctx.routeClickItem.code === route.code) ?
              { ...route.options, strokeColor: '#E40520' } : route.options,
                onClick: ($event: any) => (_ctx.$emit('routeClicked', route)),
                onMouseover: ($event: any) => (_ctx.$emit('routeHovered', route)),
                onMouseout: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('routeLeave')))
              }, null, 8, ["options", "onClick", "onMouseover"]),
              _renderSlot(_ctx.$slots, "routeInfoWindow")
            ], 64))
          }), 128))
        : _createCommentVNode("", true),
      (_ctx.ports)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.ports, (port, index) => {
              return (_openBlock(), _createBlock(_unref(CustomMarker), {
                key: index,
                options: port.options,
                onClick: ($event: any) => (_ctx.$emit('portClicked', port)),
                onMouseover: () => handlePortMouseover(port),
                onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('portLeave')))
              }, {
                default: _withCtx(() => [
                  (isNoPortSelected(port.portCode))
                    ? _renderSlot(_ctx.$slots, "customPortIcon", { key: 0 })
                    : _createCommentVNode("", true),
                  ((_ctx.portHoverItem && getIsPinItem(_ctx.portHoverItem.portCode, port.portCode))
            && !getIsPinItem(_ctx.portClickItem?.portCode, port.portCode))
                    ? _renderSlot(_ctx.$slots, "customPortIconHover", { key: 1 })
                    : _createCommentVNode("", true),
                  (_ctx.portClickItem && getIsPinItem(_ctx.portClickItem.portCode, port.portCode))
                    ? _renderSlot(_ctx.$slots, "customPortIconSelected", { key: 2 })
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["options", "onClick", "onMouseover"]))
            }), 128)),
            _renderSlot(_ctx.$slots, "portInfoWindow")
          ], 64))
        : _createCommentVNode("", true),
      (_ctx.destinations)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.destinations, (destination, index) => {
              return (_openBlock(), _createBlock(_unref(CustomMarker), {
                key: index,
                options: destination.options,
                onClick: ($event: any) => (_ctx.$emit('destinationClicked', destination)),
                onMouseover: () => handleDestinationMouseover(destination),
                onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('destinationLeave')))
              }, {
                default: _withCtx(() => [
                  (isNoDestinationSelected(destination.code))
                    ? _renderSlot(_ctx.$slots, "customDestinationIcon", { key: 0 })
                    : _createCommentVNode("", true),
                  ((_ctx.destinationHoverItem && getIsPinItem(_ctx.destinationHoverItem.code, destination.code))
            && !getIsPinItem(_ctx.destinationClickItem?.code, destination.code) )
                    ? _renderSlot(_ctx.$slots, "customDestinationIconHover", { key: 1 })
                    : _createCommentVNode("", true),
                  (_ctx.destinationClickItem && getIsPinItem(_ctx.destinationClickItem.code, destination.code))
                    ? _renderSlot(_ctx.$slots, "customDestinationIconSelected", { key: 2 })
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1032, ["options", "onClick", "onMouseover"]))
            }), 128)),
            _renderSlot(_ctx.$slots, "destinationInfoWindow")
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["api-key", "center", "zoom", "disable-default-ui", "draggable"]))
}
}

})